import { AlertTriangle, RotateCcw } from 'lucide-react';
import { presenceAgeLabel } from '@featit/shared/presence-age';
import { Button } from '@/components/Button';
import {
  loadFailureCategoryLabelIn,
  loadFailureReasonIn,
  type ILoadFailure,
} from '@/services/loadFailure';
import { useRetryOnReturn } from '@/shell/useConnection';
import { activityWords } from '@featit/shared/i18n/messages/en/activityWords';
import { banners } from '@featit/shared/i18n/messages/en/banners';
import { registerEnglishArea } from '@/i18n/catalogue';
import { useT } from '@/i18n/LocaleProvider';

registerEnglishArea('banners', banners);
registerEnglishArea('activityWords', activityWords);

/**
 * A failed read: what failed, which kind of failure, what to do, and a Try again that re-runs it
 * (I-0396).
 *
 * ⛔ **Never the rejection's own `message`.** "Request failed with status code 500" is a network log's
 * sentence; the reader gets `loadFailureReasonIn`, and the status stays out of the text.
 */
export function LoadFailureBanner({
  title,
  failure,
  retrying,
  onRetry,
  testId,
  staleSince,
}: {
  /** What failed, in plain words — "Token usage couldn't be loaded". */
  title: string;
  failure: ILoadFailure;
  retrying: boolean;
  onRetry: () => void;
  testId?: string;
  /**
   * I-1077, RD-3: when this read still has old data on screen (a refresh failed; the first load
   * never did), the ISO timestamp of that data's last successful read — so the banner says how old
   * what is showing is, rather than leaving a stale screen looking current next to an error that
   * only says the newest attempt failed. `null`/omitted (the first load itself failed, so there is
   * no old data to date) renders nothing extra, unchanged from before this prop existed.
   */
  staleSince?: string | null;
}) {
  /*
    I-1491 (RD-36 to RD-38). The chip names a cause, so it is the device's connection that earns
    "Offline", not a read that took long: a stalled read online wears no chip. Offline, Try again
    waits for the connection instead of sending a read that cannot succeed, and the read is made
    once when it returns.
  */
  const t = useT();
  const { online, waiting, press } = useRetryOnReturn(
    failure.category === 'offline' || Boolean(failure.stalled),
    onRetry,
  );
  const shown: ILoadFailure =
    !online && failure.stalled ? { ...failure, category: 'offline', stalled: false } : failure;
  const chip = failure.stalled && online ? null : loadFailureCategoryLabelIn(t, shown.category);
  return (
    <div
      role="alert"
      data-testid={testId}
      className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 rounded-md bg-red-50 px-3 py-2.5 text-[13px] text-red-800 dark:bg-red-950/40 dark:text-red-100"
    >
      {/*
        I-0855: at the largest text on a phone the message column is ~90 px, narrower than one word
        ("Unexpected"), so a word breaks rather than running past the side.
      */}
      <div className="flex min-w-0 items-start gap-2 max-sm:[overflow-wrap:anywhere]">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-medium">
            {title}
            {/*
              I-1307: a space before the chip, so a line may end between the title and it. With a
              margin alone the title's last word and the chip's first were one word to the browser,
              wider than a phone's line at the largest text size: "Unexpec / ted error".
            */}
            {chip ? (
              <>
                {' '}
                <span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-900/60 dark:text-red-200">
                  {chip}
                </span>
              </>
            ) : null}
          </p>
          {/* I-1491: offline, the chip says it; no sentence beside it explains why. */}
          {online || shown.category !== 'offline' ? (
            <p className="mt-0.5 text-red-700 dark:text-red-200">{loadFailureReasonIn(t, shown)}</p>
          ) : null}
          {staleSince ? (
            <p className="mt-0.5 text-red-700 dark:text-red-200" data-testid="failure-stale-since">
              {t('banners.loadFailure.showingDataFrom', {
                age: presenceAgeLabel(staleSince, Date.now(), t),
              })}
            </p>
          ) : null}
          {failure.reference ? (
            // I-0935, I-1470 EM-9: the server's request id, for whoever reports it; not the headline.
            // One click selects the whole id, so it is copied as one.
            <details className="mt-1 text-[12px]" data-testid="failure-reference">
              <summary className="cursor-pointer">
                {t('banners.loadFailure.technicalDetails')}
              </summary>
              <p className="mt-1">
                {t('banners.loadFailure.requestId')}{' '}
                <code className="font-mono text-[11px] break-all select-all">
                  {failure.reference}
                </code>
              </p>
            </details>
          ) : null}
        </div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        loading={retrying || waiting}
        onClick={press}
        // At the largest text on a phone its label wraps rather than running past the side (I-0855).
        className="max-w-full whitespace-normal max-sm:h-auto max-sm:min-h-touch"
      >
        {retrying ? null : <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />}
        {t('banners.loadFailure.tryAgain')}
      </Button>
    </div>
  );
}
